import { useState } from 'react';
import { TFunction } from 'i18next';
import { useLang } from 'hooks/lang';

import { InvoiceProjectByStatus, invoiceControlStatusConfig } from 'entities';

import { Column, Row, Button, Dot } from 'components/Blocks';
import { OwnerSelect } from 'components/Inputs';
import { PropertyWithIcon } from 'components/PropertyWithIcon';
import { UserAvatar } from 'components/UserAvatar';
import {
  CheckIcon,
  UserIcon,
  TicketIcon,
  UserPlusIcon,
  LocationMapIcon,
  EarthIcon,
} from 'components/Icons';

import { ContentCard, ProjectLink } from '../components';

import { useToggle } from 'hooks';

import { getCountryLabel } from 'utils/countries';

interface SingleAssignProps {
  children?: never;
  control: InvoiceProjectByStatus;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  assign: (params: { projectId: string; owner: string }) => Promise<any>;
}

const projectProps: Array<{
  getName: (t: TFunction) => string;
  extractor: (project: InvoiceProjectByStatus) => string;
  Icon: typeof UserIcon;
}> = [
  {
    getName: (t: TFunction) => t('client'),
    extractor: ({ project }) => project.clientName,
    Icon: UserIcon,
  },
  {
    getName: (t: TFunction) => t('client-origin'),
    extractor: ({ project }) => project.clientOrigin,
    Icon: LocationMapIcon,
  },
  {
    getName: (t: TFunction) => t('countries'),
    extractor: ({ project }) => project.countries.map((code) => getCountryLabel(code)).join(', '),
    Icon: EarthIcon,
  },
  {
    getName: (t: TFunction) => t('freshdesk-ticket'),
    extractor: ({ project }) => project.ticket,
    Icon: TicketIcon,
  },
];

export function SingleInvoiceAssign({ assign, control }: SingleAssignProps) {
  const [owner, setOwner] = useState('');
  const loading = useToggle();

  const { t } = useLang();

  const projectId = control.project.id;

  const executeAssign = async (): Promise<void> => {
    loading.turnOn();

    await assign({ projectId, owner });

    loading.turnOff();
  };

  return (
    <ContentCard
      header={
        <Row className="justify-between small_tablet:flex-col small_tablet:items-start" fullWidth>
          <Row gap="2">
            <Dot color={invoiceControlStatusConfig.unassigned.color} size={16} />

            <ProjectLink id={control.project.id} text={control.project.name} />
          </Row>

          {control.readyToInvoice && (
            <Row className="ml-auto text-sm small_tablet:ml-0" gap="2">
              <CheckIcon size={20} />

              {t('project-ready-to-be-invoiced')}
            </Row>
          )}
        </Row>
      }
      content={
        <Row
          alignItems="flex-start"
          justifyContent="space-between"
          fullWidth
          className="small_tablet:flex-col"
        >
          <Column gap="6">
            <PropertyWithIcon
              icon={<UserAvatar user={control.project.owner} size={30} />}
              name={t('owner')}
              value={control.project.owner.name}
            />

            {projectProps.map(({ getName, extractor, Icon }) => (
              <PropertyWithIcon
                key={getName(t)}
                icon={<Icon />}
                name={getName(t)}
                value={extractor(control)}
              />
            ))}
          </Column>

          <Column className="small_tablet:mt-2 small_tablet:w-full small_tablet:items-center">
            <OwnerSelect value={owner} onChange={setOwner} />

            {owner && (
              <Button variant="outlined" onClick={executeAssign} loading={loading.isOn}>
                <UserPlusIcon size={20} />
                {t('save')}
              </Button>
            )}
          </Column>
        </Row>
      }
    />
  );
}
